/**
 * History page content — 100% fidelity to content/company/history.md
 * Cross-reference: content/company/history.md
 * 20 sections, all handcrafted from source.
 */

export const historyMeta = {
  title: "Our Journey | CryptoMize Timeline",
  description:
    "Explore the history of CryptoMize and Perception X2. 15+ years of autonomous perception amplification — from founding vision to the world's most advanced perception platform.",
  slug: "history",
  canonical: "https://perception.ac/history",
};

export interface HeroData {
  eyebrow: string;
  title: string;
  titleAccent: string;
  lede: string;
  body: string;
  cta: { label: string; href: string }[];
  trustIndicators: string[];
  pillars?: { title: string; body: string }[];
  intro?: string;
}

export const hero: HeroData = {
  eyebrow: "Our Journey",
  title: "Our Journey. 15+ Years of",
  titleAccent: "Building the Unreplicable.",
  lede: "CryptoMize was founded on a conviction that would prove revolutionary: perception is not something that happens to you — it is something you engineer. For over 15 years, we have built, refined, and deployed the technology that proves this conviction correct.",
  body: "From a founding vision to the world's most advanced autonomous perception amplification platform, the CryptoMize story is one of relentless innovation, operational discipline, and measurable impact.",
  cta: [
    { label: "Explore Our Timeline", href: "#timeline" },
    { label: "Learn Our Mission", href: "/mission" },
  ],
  trustIndicators: [
    "15+ Years of Continuous Operation",
    "From Founding Vision to Global Platform",
    "Nine Proprietary AI Engines Developed",
    "300+ Elite Clients Served",
  ],
};

export interface FoundingData {
  eyebrow: string;
  title: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
  intro?: string;
}

export const founding: FoundingData = {
  eyebrow: "The Founding",
  title: "Where It All Began.",
  intro: "",
  pillars: [
    {
      title: "The Observation",
      body: "CryptoMize was founded on the insight that digital perception had become the defining competitive advantage for organizations worldwide. Our founders observed that the organizations with the strongest digital presence were not simply producing more content — they were engineering perception systematically.",
    },
    {
      title: "The Conviction",
      body: "In the early days of digital reputation management, most organizations treated perception as a byproduct of operations. Search results, social mentions, and news coverage were seen as outputs — not inputs. But a select group of organizations was actively shaping these outputs through increasingly sophisticated tools and strategies. The gap between those who could engineer perception and those who could not was widening daily.",
    },
    {
      title: "The Commitment",
      body: "Our founders concluded that perception management would become the most important capability for organizations across every sector. They set out to build the technology that would make autonomous perception amplification possible — not just for the largest organizations, but for every entity that needed it.",
    },
  ],
  cta: [
    { label: "Our Mission", href: "/mission" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface EarlyYearsData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const earlyYears: EarlyYearsData = {
  eyebrow: "Early Years",
  title: "Building the Foundation.",
  intro: "The early years of CryptoMize were defined by research, development, and the relentless pursuit of proprietary AI capabilities. This was not a period of rapid growth — it was a period of deep engineering.",
  pillars: [
    { title: "Research Phase", body: "CryptoMize invested years in foundational research across artificial intelligence, cognitive science, and perception engineering. Our researchers studied how perception forms, how it evolves, and how it can be systematically influenced. This research would become the intellectual foundation of Perception X2." },
    { title: "First AI Engine", body: "The development of our first AI engine marked a turning point. This engine demonstrated that autonomous perception amplification was not just theoretically possible — it was technically achievable. The success of this first engine validated our approach and accelerated development." },
    { title: "Early Client Work", body: "CryptoMize's first client engagements proved the concept in real-world conditions. These early partnerships provided the operational data and feedback that would refine our technology and approach." },
  ],
  cta: [
    { label: "Explore Our Technology", href: "/about#technology" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface PlatformDevData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const platformDevelopment: PlatformDevData = {
  eyebrow: "Platform Development",
  title: "From Engines to Platform.",
  intro: "CryptoMize evolved from building individual AI engines to developing a comprehensive autonomous perception amplification platform. This evolution represented a fundamental shift in capability and scale.",
  pillars: [
    { title: "Engine Proliferation", body: "Building on the success of our first AI engine, CryptoMize developed additional engines addressing specific dimensions of perception management. Organic Simulation. Narrative Generation. Cognitive Resonance. Echo Chamber Architecture. Autonomous Operations. Each engine solved a unique problem that no single engine could address." },
    { title: "Platform Architecture", body: "The development of Perception X2's platform architecture unified nine AI engines into a cohesive system capable of autonomous deployment across 50+ platforms. This architecture was purpose-built for scale, resilience, and performance." },
    { title: "Operational Refinement", body: "Through continuous operational refinement, CryptoMize achieved the performance standards that define Perception X2 today: 300-500% improvement in narrative penetration, 95-98% organic authenticity, and 99.9999% uptime." },
  ],
  cta: [
    { label: "Explore the Platform", href: "/about" },
    { label: "Request a Demo", href: "/demo" },
  ],
};

export interface ScalingData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const scaling: ScalingData = {
  eyebrow: "Scaling Phase",
  title: "From Proof of Concept to Global Platform.",
  intro: "CryptoMize's scaling phase marked the transition from proving the concept to delivering it at global scale. This phase was defined by infrastructure expansion, operational discipline, and client growth.",
  pillars: [
    { title: "Infrastructure Expansion", body: "CryptoMize expanded infrastructure across multiple continents, implementing redundant systems, automatic failover, and continuous monitoring. The goal: 99.9999% uptime, achievable from anywhere in the world." },
    { title: "Operational Systems", body: "We built the operational systems that enable consistent delivery across 300+ clients. Deployment workflows, quality assurance processes, monitoring infrastructure, and client success protocols — all designed for scale." },
    { title: "Client Growth", body: "As the platform's capabilities became proven, CryptoMize's client base grew from early adopters to 300+ elite organizations across governments, enterprises, and global institutions." },
  ],
  cta: [
    { label: "About Perception X2", href: "/about" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface EngineEvolutionData {
  eyebrow: string;
  title: string;
  intro: string;
  engines: { name: string; body: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const engineEvolution: EngineEvolutionData = {
  eyebrow: "AI Engine Evolution",
  title: "Nine Engines. Proprietary. Advanced. Unreplicable.",
  intro: "CryptoMize's AI engine evolution represents 15+ years of continuous development, refinement, and operational deployment. Each engine was built from the ground up, refined through real-world use, and optimized for autonomous operation.",
  engines: [
    { name: "Organic Simulation Engine", body: "Generates content statistically indistinguishable from organic engagement. This engine ensures that every deployment maintains 95-98% organic authenticity — content that no algorithm, no analyst, and no competitor can identify as engineered." },
    { name: "Narrative Generation Engine", body: "Creates and deploys narrative strategies across platforms. It analyzes audience dynamics, competitive positioning, and platform algorithms to generate narratives that penetrate and resonate." },
    { name: "Cognitive Resonance Engine", body: "Maps 144-dimensional emotional resonance data across audiences. This engine identifies the emotional, cognitive, and behavioral factors that influence perception — enabling strategies that resonate at the deepest level." },
    { name: "Echo Chamber Architecture", body: "Coordinates reinforcement across platform ecosystems. It ensures that narratives deployed on one platform are reinforced across related platforms, creating echo effects that amplify impact." },
    { name: "Autonomous Operations Engine", body: "Orchestrates cross-platform deployment without human intervention. It manages timing, sequencing, optimization, and adaptation — operating at a scale and speed that human operators cannot match." },
    { name: "Additional Engines", body: "Four additional engines complete Perception X2's capabilities: adaptive learning, competitive intelligence, threat detection, and performance optimization. Together, the nine engines deliver autonomous perception amplification at global scale." },
  ],
  cta: [
    { label: "Explore Our Technology", href: "/about#technology" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface MilestonesData {
  eyebrow: string;
  title: string;
  intro: string;
  moments: { label: string; body: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const milestoneMoments: MilestonesData = {
  eyebrow: "Milestone Moments",
  title: "Defining Points in Our Journey.",
  intro: "CryptoMize's history includes milestone moments that defined our trajectory and validated our approach.",
  moments: [
    { label: "First Enterprise Client", body: "Securing our first enterprise client proved that Perception X2's capabilities met the demanding standards of large-scale organizations. This milestone validated years of development and opened the door to enterprise growth." },
    { label: "Government Engagement", body: "Our first government engagement demonstrated that Perception X2 could meet the security, compliance, and performance standards required by sovereign entities. This milestone established CryptoMize as a trusted partner for national-level perception management." },
    { label: "Nine-Engine Platform Launch", body: "The completion of all nine AI engines marked the realization of our founding vision: a comprehensive autonomous perception amplification platform capable of operating across 50+ platforms with 95-98% organic authenticity." },
    { label: "300+ Client Milestone", body: "Reaching 300+ elite clients validated not just our technology, but our approach: measurable outcomes, operational transparency, and strategic partnership. Every client chose CryptoMize because we deliver what others cannot." },
  ],
  cta: [
    { label: "Explore Our Milestones", href: "#timeline" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface TechMilestonesData {
  eyebrow: string;
  title: string;
  intro: string;
  achievements: { label: string; body: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const techMilestones: TechMilestonesData = {
  eyebrow: "Technology Milestones",
  title: "Technical Achievements That Defined Us.",
  intro: "CryptoMize's technology milestones represent breakthroughs that advanced the state of the art in autonomous perception amplification.",
  achievements: [
    { label: "First Autonomous Deployment", body: "The first fully autonomous cross-platform deployment demonstrated that Perception X2 could operate without human intervention — coordinating across multiple platforms simultaneously while maintaining organic authenticity." },
    { label: "144-Dimensional Mapping", body: "The development of 144-dimensional emotional resonance mapping represented a breakthrough in perception engineering. This capability enables strategies that resonate at a depth and precision that conventional approaches cannot achieve." },
    { label: "50+ Platform Coverage", body: "Achieving coverage across 50+ digital platforms meant that no ecosystem was beyond Perception X2's reach. Search engines, social media, news outlets, forums, review sites, content networks — all reachable through a single platform." },
    { label: "99.9999% Uptime Achievement", body: "Reaching 99.9999% uptime — approximately 31.5 seconds of downtime per year — established Perception X2 as the most reliable perception amplification platform in existence. This standard has been maintained consistently." },
  ],
  cta: [
    { label: "Explore Our Technology", href: "/about#technology" },
    { label: "View Platform Status", href: "/status" },
  ],
};

export interface GlobalExpansionData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const globalExpansion: GlobalExpansionData = {
  eyebrow: "Global Expansion",
  title: "From Local Operations to Global Platform.",
  intro: "CryptoMize's global expansion transformed a regional operation into a global platform serving organizations across multiple continents.",
  pillars: [
    { title: "Multi-Continent Infrastructure", body: "We expanded infrastructure across multiple continents, implementing redundant systems and automatic failover. The result: consistent performance regardless of geography." },
    { title: "Multi-Language Support", body: "Perception X2 expanded to support 100+ languages, enabling perception amplification in any market. Language capability is paired with cultural context to ensure narratives resonate authentically." },
    { title: "Regional Expertise", body: "We established regional teams with local market knowledge, cultural intelligence, and regulatory expertise. Global platform, local execution — the combination that delivers outcomes everywhere." },
  ],
  cta: [
    { label: "Explore Our Operations", href: "/about" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface IndustryRecognitionData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const industryRecognition: IndustryRecognitionData = {
  eyebrow: "Industry Recognition",
  title: "How the Industry Recognized Our Work.",
  intro: "CryptoMize's innovations have been recognized by industry analysts, technology publications, and peer organizations.",
  pillars: [
    { title: "Analyst Recognition", body: "Leading technology research firms have recognized Perception X2 as a leader in autonomous perception amplification. Our platform appears in industry reports covering AI-powered perception management." },
    { title: "Media Coverage", body: "CryptoMize's innovations have attracted editorial interest from leading technology, business, and communications publications. Our platform's capabilities — nine AI engines, 144-dimensional emotional resonance mapping, 300-500% narrative penetration improvement — are regularly featured." },
    { title: "Industry Awards", body: "CryptoMize has received recognition for innovation, operational excellence, and leadership in autonomous perception amplification. These awards validate our approach and our outcomes." },
  ],
  cta: [
    { label: "Read Our Press", href: "/press" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface OperationalExcellenceData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const operationalExcellence: OperationalExcellenceData = {
  eyebrow: "Operational Excellence",
  title: "15+ Years of Consistent Delivery.",
  intro: "CryptoMize's operational track record spans 15+ years of consistent delivery, zero security incidents, and 99.9999% uptime. This track record is not accidental — it is the result of disciplined operations and relentless monitoring.",
  pillars: [
    { title: "Uptime Record", body: "99.9999% uptime — approximately 31.5 seconds of downtime per year. This standard has been maintained across 15+ years of operations, through infrastructure expansions, platform upgrades, and growing client demand." },
    { title: "Security Record", body: "Zero security incidents across 15+ years. Government-grade encryption, strict access controls, continuous threat monitoring, and security clearance for every team member. Security is a culture, not a department." },
    { title: "Client Delivery Record", body: "300+ clients served with consistent, measurable outcomes. 300-500% improvement in narrative penetration. 95-98% organic authenticity. Every client receives the same standard of delivery — regardless of size or sector." },
  ],
  cta: [
    { label: "View Platform Status", href: "/status" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface LeadershipEvolutionData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const leadershipEvolution: LeadershipEvolutionData = {
  eyebrow: "Leadership Evolution",
  title: "How Our Team Evolved with the Platform.",
  intro: "CryptoMize's leadership team has evolved alongside the platform — from founding engineers to a global executive team spanning AI, strategy, operations, and security.",
  pillars: [
    { title: "Founding Team", body: "The founding team brought deep expertise in AI engineering, cognitive science, and strategic communications. Their vision and technical capability built the foundation that Perception X2 stands on today." },
    { title: "Executive Growth", body: "As the platform scaled, CryptoMize attracted experienced executives from defense-grade AI systems, enterprise technology operations, and global strategic communications. This leadership team drives the next phase of growth." },
    { title: "Team Expansion", body: "CryptoMize's team has grown from founding engineers to a global organization of specialists. Every team member shares three characteristics: deep expertise, operational discipline, and commitment to measurable outcomes." },
  ],
  cta: [
    { label: "Meet Our Leadership", href: "/leadership" },
    { label: "Join Our Team", href: "/careers" },
  ],
};

export interface ClientJourneyData {
  eyebrow: string;
  title: string;
  intro: string;
  phases: { phase: string; body: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const clientJourneyEvolution: ClientJourneyData = {
  eyebrow: "Client Journey Evolution",
  title: "How Our Client Relationships Evolved.",
  intro: "CryptoMize's client relationships have evolved from transactional engagements to strategic partnerships.",
  phases: [
    { phase: "Early Engagements", body: "Early client relationships were focused on proving the concept: demonstrating that autonomous perception amplification could deliver measurable outcomes. These engagements validated our technology and approach." },
    { phase: "Enterprise Partnerships", body: "As Perception X2 matured, client relationships deepened into enterprise partnerships. Clients invested in long-term perception strategies, integrated Perception X2 into their operations, and relied on CryptoMize as a strategic advisor." },
    { phase: "Strategic Alliance", body: "Today, CryptoMize's most significant client relationships are strategic alliances — deep, ongoing partnerships where perception management is integrated into organizational strategy. These alliances drive the outcomes that define our reputation." },
  ],
  cta: [
    { label: "Explore Partnerships", href: "/partners" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface InnovationData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const innovation: InnovationData = {
  eyebrow: "Innovation Continuous",
  title: "Never Standing Still.",
  intro: "CryptoMize has never stopped innovating. Every year brings new capabilities, new platform features, and new performance benchmarks.",
  pillars: [
    { title: "Annual Capabilities", body: "Each year, CryptoMize adds new capabilities to Perception X2. Enhanced AI engine performance, expanded platform coverage, improved autonomous operations, and deeper integration options — delivered consistently, year after year." },
    { title: "Research Investment", body: "We invest continuously in research across AI, cognitive science, and perception engineering. This investment ensures that Perception X2 remains at the state of the art — not just today, but tomorrow." },
    { title: "Client-Driven Innovation", body: "Many of our most impactful innovations have been driven by client needs. We listen to our clients, understand their challenges, and build solutions that address those challenges directly." },
  ],
  cta: [
    { label: "Explore Our Technology", href: "/about#technology" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface ChallengesData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const challenges: ChallengesData = {
  eyebrow: "Challenges Overcome",
  title: "How We Prevailed.",
  intro: "CryptoMize's journey has not been without challenges. Our ability to overcome those challenges has defined our character and strengthened our platform.",
  pillars: [
    { title: "Technical Challenges", body: "Building nine proprietary AI engines from scratch presented technical challenges that no existing technology could solve. Our engineering team invented solutions that did not previously exist." },
    { title: "Scale Challenges", body: "Scaling from proof of concept to 300+ clients across multiple continents required operational systems that did not exist. We built them — and refined them through continuous use." },
    { title: "Market Challenges", body: "Establishing a new category of technology in a market dominated by conventional approaches required education, demonstration, and persistence. We proved our approach through results." },
  ],
  cta: [
    { label: "About Perception X2", href: "/about" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface RoadmapData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const roadmap: RoadmapData = {
  eyebrow: "Future Roadmap",
  title: "Where We Are Going Next.",
  intro: "CryptoMize's roadmap extends beyond the current state of perception management. We are building toward capabilities that will define the next generation of autonomous perception amplification.",
  pillars: [
    { title: "Next-Generation AI", body: "We are developing next-generation AI capabilities that will enhance Perception X2's performance across all nine engines — from improved emotional resonance mapping to more sophisticated autonomous operations." },
    { title: "Platform Expansion", body: "We are expanding platform coverage beyond 50+ ecosystems, adding new digital channels, emerging platforms, and next-generation content networks to our deployment capabilities." },
    { title: "Deeper Integration", body: "We are building deeper integration capabilities that enable Perception X2 to connect seamlessly with enterprise systems, marketing platforms, and communications infrastructure." },
    { title: "Advanced Intelligence", body: "We are developing advanced predictive intelligence capabilities that enable proactive perception management — identifying and addressing opportunities and threats before they materialize." },
  ],
  cta: [
    { label: "Explore Our Roadmap", href: "/mission" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface LegacyData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const legacy: LegacyData = {
  eyebrow: "Legacy",
  title: "What CryptoMize Leaves Behind.",
  intro: "CryptoMize's legacy is defined by what we have built, what we have delivered, and what we have advanced.",
  pillars: [
    { title: "Technology Legacy", body: "Perception X2 is the most advanced autonomous perception amplification platform in existence. Nine proprietary AI engines. 50+ platform coverage. 300-500% narrative penetration improvement. 95-98% organic authenticity. This platform will continue to serve organizations long into the future." },
    { title: "Client Legacy", body: "300+ organizations trust Perception X2 to shape their perception. The outcomes we have delivered have protected reputations, amplified narratives, and determined competitive positioning across governments, enterprises, and global institutions." },
    { title: "Industry Legacy", body: "CryptoMize has shaped the perception management industry through innovation, thought leadership, and ethical deployment. The standards we have set, the practices we have established, and the capabilities we have pioneered define the industry." },
  ],
  cta: [
    { label: "Our Impact", href: "/about" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface ReflectionData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const reflection: ReflectionData = {
  eyebrow: "Reflection",
  title: "What 15+ Years Have Taught Us.",
  intro: "Fifteen years of building, deploying, and refining autonomous perception amplification technology have taught CryptoMize lessons that no other organization possesses.",
  pillars: [
    { title: "Technology Lessons", body: "We have learned that perception amplification requires proprietary technology — off-the-shelf tools cannot deliver the outcomes organizations need. We have learned that autonomous operation is not optional at scale — it is essential. We have learned that organic authenticity is the difference between effective and detectable." },
    { title: "Operational Lessons", body: "We have learned that 99.9999% uptime requires redundant systems, continuous monitoring, and automatic failover. We have learned that zero security incidents requires culture, not just controls. We have learned that consistent delivery requires disciplined operations." },
    { title: "Strategic Lessons", body: "We have learned that measurable outcomes build trust. We have learned that transparency strengthens relationships. We have learned that the best technology serves clients best when paired with strategic expertise." },
  ],
  cta: [
    { label: "About Perception X2", href: "/about" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface ThankYouData {
  eyebrow: string;
  title: string;
  intro: string;
  audiences: { title: string; body: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const thankYou: ThankYouData = {
  eyebrow: "Thank You",
  title: "To Our Clients, Our Team, and Our Partners.",
  intro: "CryptoMize's journey has been shaped by the organizations that trusted us with their perception, the team members who built and operated our platform, and the partners who extended our reach.",
  audiences: [
    { title: "To Our Clients", body: "Thank you for trusting CryptoMize with your most valuable asset: your perception. Every outcome we have delivered has been earned through your trust and validated through your success." },
    { title: "To Our Team", body: "Thank you for the precision, discipline, and excellence you bring to every deployment. CryptoMize is what it is because of who you are." },
    { title: "To Our Partners", body: "Thank you for extending our capabilities and amplifying our reach. Together, we deliver outcomes that neither organization could achieve alone." },
  ],
  cta: [
    { label: "Join Us", href: "/contact" },
    { label: "Become a Partner", href: "/partners" },
  ],
};

export interface ContinueData {
  eyebrow: string;
  title: string;
  intro: string;
  body: string;
  steps: { step: string; title: string; body: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const continueJourney: ContinueData = {
  eyebrow: "Continue the Journey",
  title: "Your Perception Transformation Starts Here.",
  intro: "CryptoMize invites you to discover what autonomous perception amplification can achieve for your organization. Whether you are exploring the platform for the first time or scaling an existing partnership, the next chapter of the CryptoMize story includes you.",
  body: "Every perception challenge has a solution. Every solution begins with a conversation. Contact Perception X2 today and discover how autonomous perception amplification can reshape your digital presence with 300-500% improvement in narrative penetration and 95-98% organic authenticity.",
  steps: [
    { step: "1", title: "Request a Demo", body: "See Perception X2 in action with a personalized demonstration" },
    { step: "2", title: "Book a Consultation", body: "Speak with a senior specialist about your objectives" },
    { step: "3", title: "Submit an Inquiry", body: "Ask questions, request information, or explore opportunities" },
  ],
  cta: [
    { label: "Request a Demo", href: "/demo" },
    { label: "Book a Consultation", href: "/contact" },
    { label: "Contact Us", href: "/contact" },
  ],
};
